import { useQuery } from '@apollo/client';
import isEqual from 'lodash.isequal';
import { assert, CustomError } from '../../errors';
import {
    activeTransactionSchedulesFromGoalAccount,
    AnyDepositoryAccount,
    AnyFinancialEntity,
    getCurrentValue,
    getName,
    isPlentyGoalAccount,
    PlentyGoalAccountFinancialEntity,
    transactionSchedulesFromGoalAccount,
} from '../../financialEntities';
import { BaseGoalAccountOnboarding, Goal, GoalTransaction } from '../../goals';
import {
    CurrentRateInfo,
    FinancialEntityWithdrawalsDocument,
    GoalTargetTimeframe,
    PartnerInfo,
    PlentyJointAccountStatus,
    TransactionFieldsFragment,
    TransactionScheduleFieldsFragment,
} from '../../graphql/generated';
import { isTerminalAppTransactionStatus } from '../../transactions';
import {
    AmountRecurrence,
    getRecurrenceConfig,
    GoalAccountInvestmentType,
    GoalAccountOnboardingStep,
    PageMode,
    TransactionSchedulePlan,
} from '../../types';
import { sortBy, sum } from '../../util/arrays';
import { getFirstBusinessDay } from '../../util/businessDays';
import { toDollars } from '../../util/currency';
import { dt } from '../../util/dateTime';
import pluralize from '../../util/pluralize';
import { isNull, isNumber } from '../../util/types';
import { getErrorLogger } from '../errorLogger';
import {
    depositoryAccountsFromFEs,
    filterGoalsByShareStatus,
    getGoalAccountContributionLimitStr,
    useFinancialEntitiesContext,
} from '../financialEntities';
import { IconName } from '../icons';
import { usePageContext } from '../pageMode';
import { PartnerSwitcherType } from '../partnerships';
import {
    frequencyValueFromRecurrence,
    getExpectedArrivalDate,
    isFailedTransactionStatus,
    isObservableTransactionStatus,
} from '../transactions';
import {
    getPartnerName,
    isSingleplayer,
    useCurrentUser,
    UserInfo,
} from '../user';
import { getGoalAccounts } from './getGoalAccounts';

export const getGoalBalance = (goal: Goal): number | null => {
    const goalAccounts = goal.financialEntities.filter(isPlentyGoalAccount);
    const balances = goalAccounts.map((ga) => getCurrentValue(ga));
    return balances.length === 0 || balances.every((b) => isNull(b))
        ? null
        : sum(balances, (b) => b);
};

export const getGoalAccountCostBasis = (
    goalAccount: PlentyGoalAccountFinancialEntity
): number | null => {
    const info = goalAccount.latestSnapshot?.info;
    if (!info) return null;
    assert(info.feType === 'PlentyGoalAccount');
    return info.costBasis ?? null;
};

export interface GoalAccountInvestmentInfo {
    balance: number;
    costBasis: number;
    investmentType: GoalAccountInvestmentType;
    simpleReturn?: number;
    timeWeightedReturn?: number;
    totalInterestAmount?: number;
    totalDividendAmount?: number;
    totalFeesAmount?: number;
    realizedPNLAmount?: number;
    unrealizedPNLAmount?: number;
    contributionsAmount?: number;
    withdrawalsAmount?: number;
}

export const getGoalAccountInvestmentInfo = (
    goalAccount: PlentyGoalAccountFinancialEntity
): GoalAccountInvestmentInfo => {
    const { investmentType } = goalAccount.info;
    const balance = getGoalAccountBalance(goalAccount) ?? 0;
    const latestSnapshot = goalAccount.latestSnapshot;
    if (!latestSnapshot) return { balance, costBasis: 0, investmentType };

    const { info } = latestSnapshot;
    assert(info.feType === 'PlentyGoalAccount');

    return {
        balance,
        costBasis: info.costBasis ?? 0,
        investmentType,
        simpleReturn: info.simpleReturn,
        timeWeightedReturn: info.timeWeightedReturn,
        totalInterestAmount: info.totalInterestAmount,
        totalDividendAmount: info.totalDividendAmount,
        totalFeesAmount: info.totalFeesAmount,
        realizedPNLAmount: info.realizedPNLAmount,
        unrealizedPNLAmount: info.unrealizedPNLAmount,
        contributionsAmount: info.contributionsAmount,
        withdrawalsAmount: info.withdrawalsAmount,
    };
};

export const getGoalDepositTransactions = (
    goal: Goal
): TransactionFieldsFragment[] => {
    return getGoalAccounts(goal).flatMap(
        (fe) => fe.transactionsByDestinationId
    );
};

export const getGoalAccountGoalTransactions = (
    goalAccount: PlentyGoalAccountFinancialEntity
): GoalTransaction[] => {
    return sortBy(
        [
            // Deposits
            ...goalAccount.transactionsByDestinationId.map((transaction) => ({
                ...transaction,
                direction: 'DEPOSIT' as const,
            })),
            // Withdrawals
            ...goalAccount.transactionsBySourceId.map((transaction) => ({
                ...transaction,
                direction: 'WITHDRAWAL' as const,
            })),
        ].filter((transaction) => {
            // If transaction isn't in a state that we want observed by
            // the user, filter.
            if (!isObservableTransactionStatus(transaction.status)) {
                return false;
            }

            // If the transaction has failed and is more than 30 days
            // out-of-date, filter.
            if (
                isFailedTransactionStatus(transaction.status) &&
                dt().diff(dt(transaction.plannedProcessingDate), 'days') > 30
            ) {
                return false;
            }

            // Otherwise, show transaction.
            return true;
        }),
        'plannedProcessingDate',
        'DESC'
    );
};

export interface GoalOnboardingData {
    investmentType?: GoalAccountInvestmentType;
    riskScore: number;
    initialDepositPlan: number;
    transactionSchedulePlan: TransactionSchedulePlan;
    partnerInitialDepositPlan?: number;
    partnerTransactionSchedulePlan?: TransactionSchedulePlan;
    lastCompletedStep?: GoalAccountOnboardingStep | null;
    fundingAccountId?: string | null;
    isConfirmed?: boolean | null;
}

export const getMyGoalOnboardingData = (
    params: Goal
): Partial<GoalOnboardingData> => {
    return getGoalOnboardingDataForSubject(params, 'me');
};

export const getPartnerGoalOnboardingData = (
    params: Goal
): Partial<GoalOnboardingData> => {
    return getGoalOnboardingDataForSubject(params, 'partner');
};

const getGoalOnboardingDataForSubject = (
    goal: Goal,
    subject: 'me' | 'partner'
): Partial<GoalOnboardingData> => {
    const onboarding = getGoalAccountOnboardingForSubject(goal, subject);

    return {
        investmentType: onboarding?.info.investmentType,
        riskScore: onboarding?.info.riskScore,
        initialDepositPlan: onboarding?.info.initialDepositPlan,
        transactionSchedulePlan: onboarding?.info.transactionSchedulePlan,
        partnerInitialDepositPlan: onboarding?.info.partnerInitialDepositPlan,
        partnerTransactionSchedulePlan:
            onboarding?.info.partnerTransactionSchedulePlan,
        lastCompletedStep: onboarding?.info.lastCompletedStep,
        fundingAccountId: onboarding?.info.fundingAccountId,
        isConfirmed: !!onboarding?.confirmedAt,
    };
};

export const getMyGoalAccountOnboarding = (
    goal: Goal
): BaseGoalAccountOnboarding | undefined => {
    return getGoalAccountOnboardingForSubject(goal, 'me');
};

export const getPartnerGoalAccountOnboarding = (
    goal: Goal
): BaseGoalAccountOnboarding | undefined => {
    return getGoalAccountOnboardingForSubject(goal, 'partner');
};

export const getGoalAccountOnboardingForSubject = (
    goal: Goal,
    subject: 'me' | 'partner'
): BaseGoalAccountOnboarding | undefined => {
    const logError = getErrorLogger();

    if (goal.type === 'RETIREMENT') {
        logError(
            new Error(
                'Cannot get single goal account onboarding for a Retirement goal'
            )
        );
        return;
    }
    const onboardings = goal.goalAccountOnboardings.filter((o) =>
        subject === 'me' ? o.isMine : !o.isMine
    );
    if (onboardings.length > 1) {
        logError(
            new CustomError(
                'Cannot get single goal account onboarding for a goal with multiple goal account onboardings for user',
                { onboardings }
            )
        );
        return;
    }
    return onboardings.at(0);
};

export const getGoalBalanceForSwitcherType = (
    goal: Goal,
    partnerSwitcherType: PartnerSwitcherType
): number | undefined => {
    const myGoalAccountBalance =
        getMyGoalAccount(goal)?.latestSnapshot?.balance;
    const partnerGoalAccountBalance =
        getPartnerGoalAccount(goal)?.latestSnapshot?.balance;

    if (partnerSwitcherType === 'MINE') {
        return myGoalAccountBalance;
    }

    if (partnerSwitcherType === 'THEIRS') {
        return partnerGoalAccountBalance;
    }

    // SHARED
    return isNumber(myGoalAccountBalance) || isNumber(partnerGoalAccountBalance)
        ? (myGoalAccountBalance ?? 0) + (partnerGoalAccountBalance ?? 0)
        : undefined;
};

type GoalAccountFromGoalGetter = (
    goal: Goal
) => PlentyGoalAccountFinancialEntity | undefined;

export const getMyGoalAccount: GoalAccountFromGoalGetter = (goal) => {
    return getGoalAccounts(goal).find((fe) => fe.isMine);
};

export const getPartnerGoalAccount: GoalAccountFromGoalGetter = (goal) => {
    return getGoalAccounts(goal).find((fe) => !fe.isMine);
};

export const getJointGoalAccount: GoalAccountFromGoalGetter = (goal) => {
    return getGoalAccounts(goal).find(
        (fe) => fe.info.accountType === 'JOINT_BROKERAGE'
    );
};

export const getPossessive = ({
    partnerSwitcherType,
    partnerInfo,
    useCombined,
    useYours,
}: {
    partnerSwitcherType: PartnerSwitcherType;
    partnerInfo?: PartnerInfo | null;
    useYours?: boolean;
    useCombined?: boolean;
}) => {
    if (partnerSwitcherType === 'MINE') return useYours ? 'My' : 'Your';
    if (partnerSwitcherType === 'THEIRS')
        return `${getPartnerName(partnerInfo)}'s`;

    return useCombined ? 'Combined' : 'Shared';
};

export const getGoalAccountBalance = (
    goalAccount?: PlentyGoalAccountFinancialEntity
): number | undefined => {
    return goalAccount?.latestSnapshot?.balance;
};

export const getGoalTransactions = (goal: Goal): GoalTransaction[] => {
    return getGoalAccounts(goal).reduce<GoalTransaction[]>((txns, ga) => {
        const accountTransactions = getGoalAccountGoalTransactions(ga);
        return [...txns, ...accountTransactions];
    }, []);
};

export const getGoalTransactionSchedules = (
    goal: Goal
): TransactionScheduleFieldsFragment[] => {
    return getGoalAccounts(goal).reduce<TransactionScheduleFieldsFragment[]>(
        (txnSchedules, ga) => {
            return [
                ...txnSchedules,
                ...transactionSchedulesFromGoalAccount(ga),
            ];
        },
        []
    );
};

export const getGoalActiveTransactionSchedules = (
    goal: Goal
): TransactionScheduleFieldsFragment[] => {
    return getGoalAccounts(goal).reduce<TransactionScheduleFieldsFragment[]>(
        (txnSchedules, ga) => {
            return [
                ...txnSchedules,
                ...activeTransactionSchedulesFromGoalAccount(ga),
            ];
        },
        []
    );
};

export const getMyGoalTransactionSchedules = (
    goal: Goal
): TransactionScheduleFieldsFragment[] | undefined => {
    return transactionSchedulesFromGoalAccount(getMyGoalAccount(goal));
};

export const getPartnerGoalTransactionSchedules = (
    goal: Goal
): TransactionScheduleFieldsFragment[] | undefined => {
    return transactionSchedulesFromGoalAccount(getPartnerGoalAccount(goal));
};

/**
 * Constructs transaction schedule plans from transaction schedules.
 */
export const transactionSchedulePlansFromTransactionSchedules = (
    transactionSchedules: TransactionScheduleFieldsFragment[]
): TransactionSchedulePlan[] => {
    return transactionSchedules.map((ts) => ({
        amount: ts.amount,
        recurrence: ts.recurrence,
        startDate: ts.startDate,
    }));
};

export const getMonthlyContributionFromTransactionSchedulePlan = (
    plan: TransactionSchedulePlan
) => {
    const frequencyValue = frequencyValueFromRecurrence(plan.recurrence);

    switch (frequencyValue) {
        case 'monthly':
            return plan.amount;
        case 'weekly':
            return plan.amount * 4;
        case 'biweekly':
            return plan.amount * 2;
        case 'quarterly':
            return Math.round(plan.amount / 3);
        case 'yearly':
            return Math.round(plan.amount / 12);
        case 'onetime':
            throw new CustomError(
                'Cannot get monthly contribution from onetime plan'
            );
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = frequencyValue;
            throw new CustomError('Unsupported recurrence type', { plan });
        }
    }
};

export const getMyMonthlyContribution = (goal: Goal) => {
    const goalAccountOnboarding = getMyGoalAccountOnboarding(goal);

    const transactionSchedulePlan =
        goalAccountOnboarding?.info.transactionSchedulePlan;

    return transactionSchedulePlan
        ? getMonthlyContributionFromTransactionSchedulePlan(
              transactionSchedulePlan
          )
        : undefined;
};

export const getForecastTimingProps = (
    targetDate: string,
    forecastDate: string
): { label: string; color: string } => {
    if (targetDate === forecastDate) {
        return { label: 'on time', color: 'inherit' };
    }

    const [word, color] =
        forecastDate < targetDate
            ? ['faster', 'green.600']
            : ['slower', 'red.500'];

    const years = Math.floor(
        Math.abs(dt(targetDate).diff(dt(forecastDate), 'years', true))
    );
    if (years >= 2) {
        return { label: `${pluralize('years', years, true)} ${word}`, color };
    }

    const months = Math.floor(
        Math.abs(dt(targetDate).diff(dt(forecastDate), 'months', true))
    );
    if (months >= 1) {
        return { label: `${pluralize('months', months, true)} ${word}`, color };
    }

    // Within 1 month is considered on time
    return { label: 'on time', color: 'inherit' };
};

export const getGoalsByPartnerSwitcherType = (
    goals: Goal[]
): Record<PartnerSwitcherType, Goal[]> => {
    const goalsByPartnerSwitcherType: Record<PartnerSwitcherType, Goal[]> = {
        MINE: [],
        THEIRS: [],
        SHARED: [],
    };

    for (const goal of goals) {
        if (goal.isHidden) continue;

        if (goal.shareStatus === 'SHARED') {
            goalsByPartnerSwitcherType.MINE.push(goal);
            goalsByPartnerSwitcherType.SHARED.push(goal);
            goalsByPartnerSwitcherType.THEIRS.push(goal);
        } else if (goal.isMine) {
            goalsByPartnerSwitcherType.MINE.push(goal);
        } else {
            goalsByPartnerSwitcherType.THEIRS.push(goal);
        }
    }

    return goalsByPartnerSwitcherType;
};

export const getAmountRecurrenceStr = ({
    amount,
    recurrence: { freq, interval },
}: AmountRecurrence): string => {
    const amountStr = toDollars(amount);
    const { freqString } = getRecurrenceConfig({ freq, interval });
    return freqString.includes(' ')
        ? `${amountStr} / ${freqString}`
        : `${amountStr}/${freqString}`;
};

export const getAmountRecurrenceStrArray = (items: AmountRecurrence[]) => {
    if (items.length === 0) return [];

    // If there is one, just return the $/recurrence
    if (items.length === 1) {
        return [getAmountRecurrenceStr(items[0])];
    }

    // If all have the same recurrence, return a summed $/recurrence
    if (items.every((i, _, arr) => isEqual(i.recurrence, arr[0].recurrence))) {
        const amount = sum(items, (i) => i.amount);
        return [
            getAmountRecurrenceStr({
                amount,
                recurrence: items[0].recurrence,
            }),
        ];
    }

    // Otherwise return a list of individual $/recurrence
    return items.map(getAmountRecurrenceStr);
};

export const getUpcomingGoalTransactions = ({
    goal,
    financialEntities,
}: {
    goal: Goal;
    financialEntities: AnyFinancialEntity[];
}) => {
    const today = dt().toDateStr();
    const goalAccounts = goal.financialEntities.filter(isPlentyGoalAccount);
    const upcomingTransactions: {
        id: string;
        type: 'RECURRING' | 'ONE_TIME';
        amount: number;
        date: string;
        isMine: boolean;
        name: string;
    }[] = [];

    const getIsMine = (
        transaction: TransactionScheduleFieldsFragment | GoalTransaction
    ) => {
        const sourceFE = financialEntities.find(
            (fe) => transaction.sourceId === fe.id
        );
        const destinationFE = financialEntities.find(
            (fe) => transaction.destinationId === fe.id
        );

        // A transaction is "mine" if it was created by the current user,
        // or if the source or destination is an external joint account owned by the current user
        return !!(
            transaction.isMine ||
            (sourceFE &&
                sourceFE.type !== 'PlentyGoalAccount' &&
                sourceFE.ownersIncludeMyself) ||
            (destinationFE &&
                destinationFE.type !== 'PlentyGoalAccount' &&
                destinationFE.ownersIncludeMyself)
        );
    };

    for (const ga of goalAccounts) {
        // Collect upcoming recurring txns
        const transactionSchedules = transactionSchedulesFromGoalAccount(ga);
        transactionSchedules.forEach((transactionSchedule) => {
            if (
                transactionSchedule &&
                transactionSchedule.nextTransactionDate > today &&
                transactionSchedule.status === 'ACTIVE' &&
                transactionSchedule.amount > 0 &&
                transactionSchedule.sourceId
            ) {
                upcomingTransactions.push({
                    id: goal.id,
                    isMine: getIsMine(transactionSchedule),
                    type: 'RECURRING',
                    amount: transactionSchedule.amount,
                    date: getFirstBusinessDay(
                        dt(transactionSchedule.nextTransactionDate)
                    ).toDateStr(),
                    name: getName(ga),
                });
            }
        });

        // Collect upcoming onetime txns, if any
        const transactions = getGoalAccountGoalTransactions(ga)
            // exclude transactions associated with a transaction schedule
            .filter((t) => !t.transactionScheduleId);

        for (const t of transactions) {
            const startDt = dt(t.plannedProcessingDate);
            const sourceIsGoal = t.source?.type === 'PlentyGoalAccount';
            const destIsGoal = t.destination?.type === 'PlentyGoalAccount';
            const direction =
                sourceIsGoal && destIsGoal
                    ? 'GOAL_TO_GOAL'
                    : sourceIsGoal
                      ? 'WITHDRAWAL'
                      : 'DEPOSIT';
            const expectedArrivalDate = getExpectedArrivalDate(
                startDt,
                direction
            ).toDateStr();

            if (
                !isTerminalAppTransactionStatus(t.status) &&
                expectedArrivalDate >= dt().toDateStr()
            ) {
                upcomingTransactions.push({
                    id: goal.id,
                    type: 'ONE_TIME',
                    isMine: getIsMine(t),
                    amount: t.sourceId === ga.id ? -t.amount : t.amount,
                    date: expectedArrivalDate,
                    name: getName(ga),
                });
            }
        }
    }

    return upcomingTransactions;
};

/**
 * Considers a goal "in progress" by partner if:
 *  - Not Retirement
 *  - Not created by current user
 *  - Neither user nor partner has created a goal account for the goal
 * This applies to new SHARED goals created by partner where onboarding is not
 * yet complete, excluding existing goals (pre-new-onboarding logic) where
 * the current user has already created a goal account.
 */
export const getIsPartnerGoalOnboardingInProgress = (goal: Goal) => {
    return (
        goal.type !== 'RETIREMENT' &&
        !goal.isMine &&
        !getPartnerGoalAccountOnboarding(goal)?.financialEntityId &&
        !getMyGoalAccount(goal)
    );
};

/**
 * Conveys whether current user's partner is waiting for them to sign the Joint
 * account consent in order to open a goal account for this goal.
 */
export const getIsPartnerGoalWaitingForJointConsent = (
    goal: Goal,
    jointAccountStatus: PlentyJointAccountStatus
) => {
    return (
        getIsPartnerGoalOnboardingInProgress(goal) &&
        !!getPartnerGoalAccountOnboarding(goal)?.confirmedAt &&
        goal.accountStructure === 'JOINT' &&
        jointAccountStatus === 'PARTNER_CONSENTED'
    );
};

/**
 * Conveys whether current user is waiting for their partner to sign the Joint
 * account consent in order to open a goal account for this goal.
 */
export const getIsJointGoalWaitingForPartnerJointConsent = (
    goal: Goal,
    jointAccountStatus: PlentyJointAccountStatus
) => {
    return (
        !!getMyGoalAccountOnboarding(goal)?.confirmedAt &&
        goal.accountStructure === 'JOINT' &&
        jointAccountStatus === 'USER_CONSENTED'
    );
};

/**
 * Conveys whether a goal created by user's partner is pending Joint
 * account approval (and no further user action is necessary)
 */
export const getIsPartnerGoalPendingJointOpening = (
    goal: Goal,
    jointAccountStatus: PlentyJointAccountStatus
) => {
    return (
        getIsPartnerGoalOnboardingInProgress(goal) &&
        !!getPartnerGoalAccountOnboarding(goal)?.confirmedAt &&
        goal.accountStructure === 'JOINT' &&
        jointAccountStatus === 'OPENING'
    );
};

export const isGoalSetupIncomplete = (goal: Goal): boolean => {
    return goal.financialEntities.filter(isPlentyGoalAccount).length === 0;
};

export const getGoalSetupIncompleteLabel = ({
    goal,
    isMobile,
}: {
    goal: Goal;
    isMobile: boolean;
}): string => {
    const { lastCompletedStep, isConfirmed } = getMyGoalOnboardingData(goal);

    if (isConfirmed) {
        return 'Plan confirmed';
    }

    if (!lastCompletedStep) {
        return 'Start investing';
    }

    if (goal.onboardingVersion === 'v1') {
        return isMobile ? 'Finish on web' : 'Finish setup';
    }

    // goal.onboardingVersion === 'v2'
    return isMobile ? 'Finish setup' : 'Finish setup on mobile';
};

/**
 * Returns decimal percentage representing goal progress
 */
export const getProgressUsingTargetAmount = ({
    goal,
}: {
    goal: Goal;
    userInfo: UserInfo;
    currentRateInfo: CurrentRateInfo;
}): number => {
    if (!goal.targetAmount) return 0;

    const goalBalance = getGoalBalanceForSwitcherType(goal, 'SHARED');
    if (!goalBalance) return 0;

    // TODO: Consider including pending transfers in balance

    return (goalBalance / goal.targetAmount) * 100;
};

/**
 * Returns current money market rate as a percentage, rounded to two decimals
 *
 * Example: 4.33%
 */
export const getMoneyMarketRateStr = (currentRateInfo: {
    moneyMarketRate: number;
}) => {
    return `${(currentRateInfo.moneyMarketRate * 100).toFixed(2)}%`;
};

export const getContributedOrInvestedStr = (
    goalAccountInvestmentType: GoalAccountInvestmentType
) => {
    return goalAccountInvestmentType === 'HIGH_YIELD_CASH'
        ? 'contributed'
        : 'invested';
};

export const getContributionOrInvestmentStr = (
    goalAccountInvestmentType: GoalAccountInvestmentType
) => {
    return goalAccountInvestmentType === 'HIGH_YIELD_CASH'
        ? 'contribution'
        : 'investment';
};

export const getContributingOrInvestingStr = (
    goalAccountInvestmentType: GoalAccountInvestmentType
) => {
    return goalAccountInvestmentType === 'HIGH_YIELD_CASH'
        ? 'contributing'
        : 'investing';
};

export const iconByGoalAccountInvestmentType: Record<
    GoalAccountInvestmentType,
    IconName
> = {
    HIGH_YIELD_CASH: 'CashPlus',
    CLASSIC_PORTFOLIO: 'ClassicPortfolio',
    DIRECT_INDEXING: 'PremiumPortfolio',
};

export const titleByGoalAccountInvestmentType: Record<
    GoalAccountInvestmentType,
    string
> = {
    HIGH_YIELD_CASH: 'Cash +',
    DIRECT_INDEXING: 'Premium portfolio',
    CLASSIC_PORTFOLIO: 'Classic portfolio',
};

type GoalAccountOwnershipDetails =
    | {
          ownershipType: 'JOINT';
          goalAccount: PlentyGoalAccountFinancialEntity;
          balance?: number;
      }
    | {
          ownershipType: 'SPLIT';
          myGoalAccount?: PlentyGoalAccountFinancialEntity;
          partnerGoalAccount?: PlentyGoalAccountFinancialEntity;
          myBalance?: number;
          partnerBalance?: number;
      }
    | {
          ownershipType: 'PRIVATE';
          goalAccount: PlentyGoalAccountFinancialEntity;
          balance?: number;
      };

export const getGoalAccountOwnershipDetails = (
    goalUnfiltered: Goal
): GoalAccountOwnershipDetails | undefined => {
    const logError = getErrorLogger();
    const goalAccounts = getGoalAccounts(goalUnfiltered);
    if (!goalAccounts.at(0)) return;

    const accountType = goalAccounts[0].info.accountType;
    const sharingStatus = goalUnfiltered.shareStatus;

    if (accountType === 'JOINT_BROKERAGE') {
        // Goal account ownership structure is Joint
        return {
            ownershipType: 'JOINT',
            goalAccount: goalAccounts[0],
            balance: getGoalAccountBalance(goalAccounts[0]),
        };
    }

    // TODO: Handle IRA/Retirement accounts once we revisit supporting those type of accounts in the future
    if (accountType === 'ROTH_IRA' || accountType === 'TRADITIONAL_IRA') {
        return;
    }

    // If we're here, must be BROKERAGE

    if (sharingStatus === 'SHARED') {
        // Goal account ownership structure is Split
        const myGoalAccount = getMyGoalAccount(goalUnfiltered);
        const partnerGoalAccount = getPartnerGoalAccount(goalUnfiltered);
        return {
            ownershipType: 'SPLIT',
            myGoalAccount,
            myBalance: getGoalAccountBalance(myGoalAccount),
            partnerGoalAccount,
            partnerBalance: getGoalAccountBalance(partnerGoalAccount),
        };
    } else {
        // Goal account ownership structure is Private
        const myGoalAccount = getMyGoalAccount(goalUnfiltered);
        if (!myGoalAccount) {
            logError(
                new CustomError(
                    'Attempted to show goal account info for Private goal, but goal account for current user was missing',
                    { goalUnfiltered }
                )
            );
            return;
        }
        return {
            ownershipType: 'PRIVATE',
            goalAccount: myGoalAccount,
            balance: getGoalAccountBalance(myGoalAccount),
        };
    }
};

export const getGoalBalanceForPageMode = ({
    details,
    pageMode,
}: {
    details: GoalAccountOwnershipDetails;
    pageMode: PageMode;
}) => {
    let balance: number;
    let suffixBalance: number | undefined;

    if (
        details.ownershipType === 'JOINT' ||
        details.ownershipType === 'PRIVATE'
    ) {
        balance = details.balance ?? 0;
    } else {
        const totalBalance =
            (details.myBalance ?? 0) + (details.partnerBalance ?? 0);
        if (pageMode === 'MINE') {
            balance = details.myBalance ?? 0;
            suffixBalance = totalBalance;
        } else {
            balance = totalBalance;
        }
    }

    return { balance, suffixBalance };
};

export const sortGoalsByImportance = (goals: Goal[]): Goal[] => {
    const sortedByCreation = sortBy(goals, (g) => g.createdAt, 'DESC');
    const withBalances: Goal[] = [];
    const withoutBalances: Goal[] = [];
    sortedByCreation.forEach((g) =>
        isNumber(getGoalBalance(g))
            ? withBalances.push(g)
            : withoutBalances.push(g)
    );
    return [
        ...sortBy(withBalances, (g) => getGoalBalance(g) ?? 0, 'DESC'),
        ...withoutBalances,
    ];
};

/** Returns goals filtered by share status at the *goal* level, but keeps child
 * *goal accounts* unfiltered to ensure consistent experience between page modes
 * for user. Useful in Dashboard carousel and Goals page tiles.
 * TODO: Reconsider how we expose goals from the financial entities context;
 * should they always include all their goal accounts?
 */
export const useSortedUnfilteredGoalsForPageMode = () => {
    const { fes } = useFinancialEntitiesContext();
    const { pageMode } = usePageContext();
    const currentUser = useCurrentUser();

    const loading = fes.loading;
    const goals = fes.unfilteredData?.goals;

    if (loading || !goals) return { loading: true, goals: null };

    const unsortedGoals = isSingleplayer(currentUser)
        ? goals
        : filterGoalsByShareStatus(pageMode, goals);

    return {
        loading: false,
        goals: sortGoalsByImportance(unsortedGoals),
    };
};

export const useGoalAccount = (
    financialEntityId: string
): PlentyGoalAccountFinancialEntity | undefined => {
    const { fes } = useFinancialEntitiesContext();

    return fes.data?.goals
        ?.flatMap(getGoalAccounts)
        .find((fe) => fe.id === financialEntityId);
};

export const useDepositoryAccount = (
    financialEntityId?: string | null
): AnyDepositoryAccount | undefined => {
    const { fes } = useFinancialEntitiesContext();
    if (!fes.unfilteredData || !financialEntityId) return;

    return depositoryAccountsFromFEs(fes.unfilteredData.financialEntities).find(
        (account) => account.id === financialEntityId
    );
};

export const useGoalAccountContributionLimitStr = (
    financialEntityId: string
): { contributionLimitStr: string | undefined } => {
    const goalAccount = useGoalAccount(financialEntityId);
    const contributionLimitStr = goalAccount
        ? getGoalAccountContributionLimitStr(goalAccount)
        : undefined;
    return { contributionLimitStr };
};

export const useOnetimeTransactionSourceBalance = (
    sourceId: string
): { sourceBalance: number | undefined } => {
    // Source could be a depository account or a goal account
    const depositoryAccount = useDepositoryAccount(sourceId);
    const goalAccount = useGoalAccount(sourceId);

    const sourceBalance = depositoryAccount
        ? depositoryAccount.latestSnapshot?.balance
        : getGoalAccountBalance(goalAccount);

    return { sourceBalance };
};

export const usePendingWithdrawalAmount = (
    financialEntityId?: string
): {
    pendingWithdrawalAmount: number | null;
} => {
    // This fetches the 10 latest withdrawals which should be sufficient
    // to capture all of the pending ones.
    // TODO: Consider exposing an efficient way to filter these server-side
    const { data } = useQuery(FinancialEntityWithdrawalsDocument, {
        variables: { financialEntityId: financialEntityId ?? '' },
        fetchPolicy: 'no-cache',
        skip: !financialEntityId,
    });

    if (!data) return { pendingWithdrawalAmount: null };

    const pendingWithdrawalAmount = sum(
        data.transactions?.filter(
            ({ status }) => !isTerminalAppTransactionStatus(status)
        ) ?? [],
        (transaction) => transaction.amount
    );

    return { pendingWithdrawalAmount };
};

export const useGoalFromGoalAccountId = (
    financialEntityId: string
): Goal | undefined => {
    const { fes } = useFinancialEntitiesContext();

    return fes.data?.goals?.find((goal) =>
        getGoalAccounts(goal).find((fe) => fe.id === financialEntityId)
    );
};

export const getFullWithdrawalDescription = ({
    amount,
    sourceBalance,
    sourceGoalAccountInvestmentType,
    pendingWithdrawalAmount,
}: {
    amount: number;
    sourceBalance: number | null | undefined;
    sourceGoalAccountInvestmentType: GoalAccountInvestmentType;
    pendingWithdrawalAmount: number | null | undefined;
}): string | undefined => {
    const isFullWithdrawal =
        isNumber(sourceBalance) &&
        isNumber(pendingWithdrawalAmount) &&
        amount === sourceBalance - pendingWithdrawalAmount;

    if (!isFullWithdrawal) return;

    return sourceGoalAccountInvestmentType === 'HIGH_YIELD_CASH'
        ? `The actual amount may be slightly higher or lower due to interest and fees.`
        : `The actual amount may be slightly higher or lower due to market fluctuations between now and when your individual stocks are sold.`;
};

export const getWhenStr = (createdAt: string) => {
    const createdAtDT = dt(createdAt);
    return createdAtDT.isBefore(formatCutoffDT)
        ? createdAtDT.format('MMM DD')
        : createdAtDT.fromNow();
};

export const formatCutoffDT = dt().startOf('day').subtract(2, 'days');

export const TARGET_TIMEFRAME_OPTIONS: {
    value: GoalTargetTimeframe;
    label: string;
    description: string;
}[] = [
    {
        value: 'ZERO_TO_ONE_YEARS',
        label: '0-1',
        description: 'Years',
    },
    {
        value: 'ONE_TO_TWO_YEARS',
        label: '1-2',
        description: 'Years',
    },
    {
        value: 'TWO_TO_FIVE_YEARS',
        label: '2-5',
        description: 'Years',
    },
    {
        value: 'FIVE_PLUS_YEARS',
        label: '5+',
        description: 'Years',
    },
];
